import { all, put, race, select, take, takeLatest } from 'redux-saga/effects'
import { Map } from 'immutable'
import _ from 'lodash'
import { SUCCESS, FAILED } from 'crom-api-constants'

import config from '../../../../../../../../config'

import { USER_PROFILE_LOAD_CONTENT } from '../Loader/constants'
import { MENU_ITEM_KEYS } from '../../../../constants'
import {
    USER_PROFILE_LICENSE_LIMITS_REQUEST,
    USER_PROFILE_MONITORING_STATS_REQUEST,
    USER_PROFILE_PORTFOLIO_STATS_REQUEST,
    USER_PROFILE_DISTRAINTS_STATS_REQUEST,
} from './constants'

import { contentLoaded } from '../Loader/actions'

import { licenseLimitRequest, monitoringStatsRequest, portfolioStatsRequest, distraintsStatsRequest } from './actions'

import { sessionIdSelector } from './selectors'
import { hasRightsSelector } from '../../../../../../../../services/acl'

const USER_PROFILE_LICENSE_LIMITS_REQUEST_SUCCESS = `${USER_PROFILE_LICENSE_LIMITS_REQUEST}_${SUCCESS}`
const USER_PROFILE_LICENSE_LIMITS_REQUEST_FAILED = `${USER_PROFILE_LICENSE_LIMITS_REQUEST}_${FAILED}`
const USER_PROFILE_MONITORING_STATS_REQUEST_SUCCESS = `${USER_PROFILE_MONITORING_STATS_REQUEST}_${SUCCESS}`
const USER_PROFILE_MONITORING_STATS_REQUEST_FAILED = `${USER_PROFILE_MONITORING_STATS_REQUEST}_${FAILED}`
const USER_PROFILE_PORTFOLIO_STATS_REQUEST_SUCCESS = `${USER_PROFILE_PORTFOLIO_STATS_REQUEST}_${SUCCESS}`
const USER_PROFILE_PORTFOLIO_STATS_REQUEST_FAILED = `${USER_PROFILE_PORTFOLIO_STATS_REQUEST}_${FAILED}`
const USER_PROFILE_DISTRAINTS_STATS_REQUEST_SUCCESS = `${USER_PROFILE_DISTRAINTS_STATS_REQUEST}_${SUCCESS}`
const USER_PROFILE_DISTRAINTS_STATS_REQUEST_FAILED = `${USER_PROFILE_DISTRAINTS_STATS_REQUEST}_${FAILED}`

const periodOrder = ['year', 'month', 'week', 'day']
function getReportAndLinkagesValues(data) {
    const usedPeriod =
        _.find(periodOrder, (period) => _.get(data, `${period}Limit`, null) !== null) || _.first(periodOrder)

    return Map({
        value: _.get(data, `${usedPeriod}Counter`, 0),
        total: _.get(data, `${usedPeriod}Limit`, 0),
        period: usedPeriod,
    })
}

function isGeneralSettingsLoadAction(_action) {
    const actionType = _.get(_action, 'type', null)
    const contentId = _.get(_action, 'data.id', null)

    return actionType === USER_PROFILE_LOAD_CONTENT && contentId === MENU_ITEM_KEYS.GENERAL
}

function* loadGeneralSettingsContent(_action) {
    const contentId = _.get(_action, 'data.id', null)

    const sessionId = yield select(sessionIdSelector)
    const hasReportRights = yield select(hasRightsSelector, config.modules.report.key)
    const hasLinkagesRights = yield select(hasRightsSelector, config.modules.linkages.key)
    const hasLicenseLimitsRights = hasReportRights || hasLinkagesRights
    const hasMonitoringStatsRights = yield select(hasRightsSelector, config.modules.monitoring.key)
    const hasPortfolioStatsRights = yield select(hasRightsSelector, config.modules.portfolio.key)
    const hasDistraintsStatsRights = yield select(hasRightsSelector, config.modules.distraints.key)

    const results = yield all({
        responses: all({
            licenseLimits: hasLicenseLimitsRights
                ? race({
                      success: take(USER_PROFILE_LICENSE_LIMITS_REQUEST_SUCCESS),
                      failed: take(USER_PROFILE_LICENSE_LIMITS_REQUEST_FAILED),
                  })
                : null,
            monitoringStats: hasMonitoringStatsRights
                ? race({
                      success: take(USER_PROFILE_MONITORING_STATS_REQUEST_SUCCESS),
                      failed: take(USER_PROFILE_MONITORING_STATS_REQUEST_FAILED),
                  })
                : null,
            portfolioStats: hasPortfolioStatsRights
                ? race({
                      success: take(USER_PROFILE_PORTFOLIO_STATS_REQUEST_SUCCESS),
                      failed: take(USER_PROFILE_PORTFOLIO_STATS_REQUEST_FAILED),
                  })
                : null,
            distraintsStats: hasDistraintsStatsRights
                ? race({
                      success: take(USER_PROFILE_DISTRAINTS_STATS_REQUEST_SUCCESS),
                      failed: take(USER_PROFILE_DISTRAINTS_STATS_REQUEST_FAILED),
                  })
                : null,
        }),
        requests: all([
            hasLicenseLimitsRights ? put(licenseLimitRequest(sessionId)) : null,
            hasMonitoringStatsRights ? put(monitoringStatsRequest(sessionId)) : null,
            hasPortfolioStatsRights ? put(portfolioStatsRequest()) : null,
            hasDistraintsStatsRights ? put(distraintsStatsRequest()) : null,
        ]),
    })

    const response = results.responses
    const reportResponse =
        _.find(_.get(response, 'licenseLimits.success.payload.response.body', []), ['moduleCd', 'REPO']) || null
    const linkagesResponse =
        _.find(_.get(response, 'licenseLimits.success.payload.response.body', []), ['moduleCd', 'LIAN']) || null
    const monitoringBody = _.get(response, 'monitoringStats.success.payload.response.body', null)
    const monitoringResponse = monitoringBody ? _.first(monitoringBody) || {} : null
    const portfolioResponse = _.get(response, 'portfolioStats.success.payload.response.body', null)
    const distraintsResponse = _.get(response, 'distraintsStats.success.payload.response.body.data', null)

    const contentData = Map({
        report: reportResponse ? getReportAndLinkagesValues(reportResponse) : null,
        linkages: linkagesResponse ? getReportAndLinkagesValues(linkagesResponse) : null,
        monitoring: monitoringResponse
            ? Map({
                  subjects: Map({
                      value: monitoringResponse.licenceEntityCount || 0,
                      total: monitoringResponse.licenceEntityLimit || 0,
                  }),
                  groups: Map({
                      value: monitoringResponse.userQueryCount || 0,
                      total: monitoringResponse.userQueryLimit || 0,
                  }),
              })
            : null,
        portfolio: portfolioResponse
            ? Map({
                  value: portfolioResponse.creditsDrawn || 0,
                  total: portfolioResponse.creditsLimit || 0,
                  name: _.get(portfolioResponse, 'product.name', ''),
              })
            : null,
        distraints: distraintsResponse
            ? Map({
                  cz: Map(distraintsResponse.cz),
                  sk: Map(distraintsResponse.sk),
              })
            : null,
    })

    yield put(contentLoaded(contentId, contentData))

    return null
}

export default function* generalSettingsSaga() {
    yield all([takeLatest(isGeneralSettingsLoadAction, loadGeneralSettingsContent)])
}
